// Literal process.env access so Next inlines the value into client bundles;
// dynamic env access (siteConfig.gadsId) is empty in the browser.
const GOOGLE_ADS_ID = process.env.NEXT_PUBLIC_GOOGLE_ADS_ID ?? "";
// Fallback is the live tag of Roma's standalone Ads account (2775186950), so
// the Roma host tracks correctly even when the env override is not configured.
const ROMA_GOOGLE_ADS_ID =
  process.env.NEXT_PUBLIC_ROMA_GOOGLE_ADS_ID ?? "AW-18333212236";

type ConversionPayloadInput = {
  conversionLabel?: string;
  value?: number;
  currency?: string;
  transactionId?: string;
};

type EnhancedConversionUserInput = {
  email?: string;
  phone?: string;
};

type SendConversionInput = ConversionPayloadInput & {
  userData?: EnhancedConversionUserInput;
};

export const privateEventsConversionLabel =
  process.env.NEXT_PUBLIC_GOOGLE_ADS_PRIVATE_EVENTS_CONVERSION_LABEL ?? "";

export const tuscanReservationConversionLabel =
  process.env.NEXT_PUBLIC_GOOGLE_ADS_RESERVATION_CONVERSION_LABEL ?? "";

export const tuscanOrderConversionLabel =
  process.env.NEXT_PUBLIC_GOOGLE_ADS_ORDER_CONVERSION_LABEL ?? "";

// Fallback is the "Reservation Completed" conversion label in Roma's
// standalone Ads account; override via env if the action is ever recreated.
const romaReservationConversionLabel =
  process.env.NEXT_PUBLIC_ROMA_GOOGLE_ADS_RESERVATION_CONVERSION_LABEL ??
  "Nx0uCI6m4NIcEMy8-qVE";

const enhancedConversionsEnabled =
  process.env.NEXT_PUBLIC_GOOGLE_ADS_ENHANCED_CONVERSIONS === "true";

function getProductionHostname() {
  if (typeof window === "undefined") return "";
  return window.location?.hostname.toLowerCase().replace(/^www\./, "") ?? "";
}

function normalizePhoneNumber(phone?: string) {
  const digits = (phone ?? "").replace(/\D/g, "");

  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith("1")) return `+${digits}`;
  if ((phone ?? "").trim().startsWith("+") && digits.length >= 8) {
    return `+${digits}`;
  }

  return "";
}

export function buildEnhancedConversionUserData(input: EnhancedConversionUserInput) {
  const email = input.email?.trim().toLowerCase();
  const phoneNumber = normalizePhoneNumber(input.phone);
  const userData: Record<string, string> = {};

  if (email) userData.email = email;
  if (phoneNumber) userData.phone_number = phoneNumber;

  return Object.keys(userData).length ? userData : null;
}

export function buildGoogleAdsConversionPayload(
  input: ConversionPayloadInput,
  googleAdsId = GOOGLE_ADS_ID,
) {
  const conversionLabel = input.conversionLabel?.trim();

  if (!googleAdsId || !conversionLabel) return null;

  return {
    send_to: `${googleAdsId}/${conversionLabel}`,
    ...(typeof input.value === "number" ? { value: input.value } : {}),
    ...(input.currency ? { currency: input.currency } : {}),
    ...(input.transactionId ? { transaction_id: input.transactionId } : {}),
  };
}

export function sendRomaReservationGoogleAdsConversion(input: {
  transactionId: string;
  email?: string;
  phone?: string;
}) {
  if (getProductionHostname() !== "chefsromakitchen.com") return false;

  return sendGoogleAdsConversion(
    {
      conversionLabel: romaReservationConversionLabel,
      value: 1,
      currency: "USD",
      transactionId: input.transactionId,
      userData: { email: input.email, phone: input.phone },
    },
    ROMA_GOOGLE_ADS_ID,
  );
}

/**
 * Roma Henderson completes reservations on Google Reserve, outside this site's origin; Southern Highlands books first-party and fires sendRestaurantReservationGoogleAdsConversion instead.
 * The live Google Ads conversion action is configured as an onclick action, so
 * the measurable website conversion is the final outbound reservation handoff.
 */
export function sendRomaReservationClickGoogleAdsConversion() {
  if (getProductionHostname() !== "chefsromakitchen.com") return false;

  return sendGoogleAdsConversion(
    {
      conversionLabel: romaReservationConversionLabel,
      value: 1,
      currency: "USD",
    },
    ROMA_GOOGLE_ADS_ID,
  );
}

export function sendTuscanReservationGoogleAdsConversion(input: {
  transactionId: string;
  email?: string;
  phone?: string;
}) {
  if (getProductionHostname() !== "tuscancove.com") return false;

  return sendGoogleAdsConversion({
    conversionLabel: tuscanReservationConversionLabel,
    transactionId: input.transactionId,
    userData: { email: input.email, phone: input.phone },
  });
}

export function sendRestaurantReservationGoogleAdsConversion(input: {
  transactionId: string;
  email?: string;
  phone?: string;
}) {
  const hostname = getProductionHostname();

  if (hostname === "tuscancove.com") {
    return sendTuscanReservationGoogleAdsConversion(input);
  }
  if (hostname === "chefsromakitchen.com") {
    return sendRomaReservationGoogleAdsConversion(input);
  }

  return false;
}

export function sendTuscanOrderGoogleAdsConversion(input: {
  transactionId: string;
  value: number;
  email?: string;
  phone?: string;
}) {
  if (getProductionHostname() !== "tuscancove.com") return false;
  if (!Number.isFinite(input.value) || input.value <= 0) return false;

  return sendGoogleAdsConversion({
    conversionLabel: tuscanOrderConversionLabel,
    value: input.value,
    currency: "USD",
    transactionId: input.transactionId,
    userData: { email: input.email, phone: input.phone },
  });
}

export function sendGoogleAdsConversion(
  input: SendConversionInput,
  googleAdsId = GOOGLE_ADS_ID,
) {
  if (typeof window === "undefined" || !window.gtag) return false;

  const payload = buildGoogleAdsConversionPayload(input, googleAdsId);
  if (!payload) return false;

  const userData = input.userData ? buildEnhancedConversionUserData(input.userData) : null;
  if (enhancedConversionsEnabled && userData) {
    window.gtag("set", "user_data", userData);
  }

  window.gtag("event", "conversion", payload);

  return true;
}
